import { useCallback, useRef, useState } from 'react';
import ConfirmModal from '../ConfirmModal/ConfirmModal';

/**
 * `window.confirm`, in-app.
 *
 * A native confirm is a different dialog from every other one in the suite: it cannot be styled,
 * it is drawn by the browser rather than the page, and embedded browsers suppress it entirely —
 * which reads to the user as a button that does nothing. This gives the same shape of call, so a
 * site converts in one line and keeps its control flow:
 *
 *   const [ask, confirmDialog] = useConfirmDialog();
 *   ...
 *   if (!(await ask({ title: 'Delete this snapshot?', message: 'This cannot be undone.' }))) return;
 *   ...
 *   return (<>{confirmDialog}...</>);
 *
 * `ask` resolves true on confirm and false on cancel, Escape, or a click outside — so an
 * unanswered dialog is a "no", exactly as the native one behaved. Render `confirmDialog` once
 * anywhere in the component.
 */
export function useConfirmDialog() {
  const [config, setConfig] = useState(null);
  // The pending promise's resolve, kept out of state: settling is a side effect, and doing it
  // inside a state updater would run it again on every re-render in StrictMode.
  const resolveRef = useRef(null);

  const ask = useCallback((next = {}) => new Promise((resolve) => {
    resolveRef.current = resolve;
    setConfig(next);
  }), []);

  const settle = useCallback((answer) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setConfig(null);
    if (resolve) resolve(answer);
  }, []);

  const confirmDialog = (
    <ConfirmModal
      open={!!config}
      hideNote
      title={(config && config.title) || 'Are you sure?'}
      message={(config && config.message) || ''}
      details={(config && config.details) || null}
      confirmLabel={(config && config.confirmLabel) || 'Confirm'}
      closeLabel={(config && config.closeLabel) || 'Cancel'}
      onConfirm={() => settle(true)}
      onClose={() => settle(false)}
    />
  );

  return [ask, confirmDialog];
}

export default useConfirmDialog;
